/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Whisper Transcription Service API
 * OpenAPI contract for the Whisper transcription service. This defines the interface between the Kotlin backend and the Python Whisper service for audio transcription with speaker diarization.  ## Authentication  The service uses Firebase JWT tokens for authentication. Include the token in the `Authorization` header as a Bearer token: ``` Authorization: Bearer <firebase-id-token> ```  Internal endpoints (`/internal/_*`) use service-to-service authentication via the `X-Service-Key` header.  ## Audio Upload Patterns  ### File Upload (Pre-recorded) 1. Call `POST /api/v1/uploads/initiate` to get a signed URL 2. Upload directly to GCS using the signed URL (resumable upload) 3. GCS Object Finalize event triggers processing automatically 4. The Kotlin backend fetches results via the internal `GET /internal/transcripts/{id}` endpoint  ### Live Recording (WebSocket) Connect to `wss://whisper.scrivio.nl/api/v1/recordings/live` with the Firebase token.  #### WebSocket Message Format  **Client → Server:** ```json {   \"type\": \"audio\",   \"data\": \"<base64-encoded-pcm-audio>\" } ``` Audio format: 16kHz, mono, Int16 PCM (no container format).  ```json {   \"type\": \"end\" } ``` Signals end of recording.  **Server → Client:** ```json {   \"type\": \"partial\",   \"text\": \"Partial transcription...\" } ``` Real-time partial transcription (may change).  ```json {   \"type\": \"segment\",   \"segment\": {     \"speakerLabel\": 0,     \"text\": \"Final segment text\",     \"startTimeMs\": 0,     \"endTimeMs\": 2500   } } ``` Finalized transcript segment.  ```json {   \"type\": \"complete\",   \"transcriptId\": \"550e8400-e29b-41d4-a716-446655440000\" } ``` Transcription completed; use the ID to fetch full results.  ```json {   \"type\": \"error\",   \"message\": \"Error description\" } ``` Processing error occurred. 
 *
 * The version of the OpenAPI document: 1.1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Speaker diarization engine to identify different speakers in the audio: - `pyannote-3.1`: High-quality diarization using pyannote.audio 3.1 - `none`: Disable diarization (single speaker assumed)  Omit to let the service pick: the engine defaults to the deployment\'s configured `DEFAULT_DIARIZATION_ENGINE`. Naming a fixed default here instead would override that per-deployment setting (ADR-022).  A name in this enum is one the service can actually run — that is what `tests/test_upload_contract.py` holds. `nemo` was listed here until #4248 and is gone with the engine behind it (ADR-022); a request naming it now fails contract validation with 422 rather than the transcription. 
 */

export const DiarizationEngine = {
    Pyannote31: 'pyannote-3.1',
    None: 'none'
} as const;

export type DiarizationEngine = typeof DiarizationEngine[keyof typeof DiarizationEngine];


/**
 * Response after processing GCS event
 */
export interface GcsEventResponse {
    /**
     * ID of the transcript that will be processed
     */
    'transcriptId': string;
    /**
     * Status of the event processing
     */
    'status': string;
}
/**
 * GCS Object Finalize event from Eventarc
 */
export interface GcsObjectFinalizeEvent {
    /**
     * GCS bucket name
     */
    'bucket': string;
    /**
     * Object name (path within bucket)
     */
    'name': string;
    /**
     * MIME type of the uploaded file
     */
    'contentType'?: string;
    /**
     * File size in bytes (as string per GCS convention)
     */
    'size'?: string;
    /**
     * Object creation timestamp
     */
    'timeCreated'?: string;
}
/**
 * Health check response
 */
export interface HealthResponse {
    /**
     * Health status of the service
     */
    'status': HealthResponseStatusEnum;
    /**
     * Whether the service is ready for transcription requests. False while ML models are still loading during cold start. Clients should wait for ready=true before connecting WebSocket. 
     */
    'ready': boolean;
    /**
     * Number of streaming sessions this instance is currently holding — one per live recording. The service runs at max_instance_count = 1, so a deploy drains every one of them; `whisper:deploy` reads this field and refuses to cut the instance over while it is non-zero (#3677). 
     */
    'activeStreamingSessions': number;
    /**
     * Additional health information or error details
     */
    'message'?: string | null;
}

export const HealthResponseStatusEnum = {
    Healthy: 'healthy',
    Unhealthy: 'unhealthy'
} as const;

export type HealthResponseStatusEnum = typeof HealthResponseStatusEnum[keyof typeof HealthResponseStatusEnum];

/**
 * Request to initiate an audio file upload. Exactly one of `appointmentId` or `formSubmissionId` must be provided. 
 */
export interface InitiateUploadRequest {
    /**
     * Appointment this recording is associated with. Mutually exclusive with formSubmissionId. 
     */
    'appointmentId'?: string | null;
    /**
     * Form submission this recording is associated with. Mutually exclusive with appointmentId. 
     */
    'formSubmissionId'?: string | null;
    /**
     * Original filename of the audio file
     */
    'filename': string;
    /**
     * MIME type of the audio file
     */
    'contentType': string;
    /**
     * Language code for transcription (BCP 47 format)
     */
    'language'?: string;
    /**
     * Expected number of speakers (1-10). Speeds up diarization when known.
     */
    'speakerCount'?: number;
    'transcriptionEngine'?: TranscriptionEngine;
    'diarizationEngine'?: DiarizationEngine;
}


/**
 * Liveness response. Deliberately carries nothing the database could answer differently — the fields a caller wants about *readiness* are on HealthResponse, and a liveness probe that read them would recycle a container over a stall a restart cannot fix (#3872). 
 */
export interface LivenessResponse {
    /**
     * Always `alive` — reaching this endpoint is the assertion.
     */
    'status': string;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Response containing transcript segments
 */
export interface SegmentsResponse {
    /**
     * ID of the transcript these segments belong to
     */
    'transcriptId': string;
    /**
     * Time-stamped transcript segments with speaker labels
     */
    'segments': Array<TranscriptSegment>;
}
/**
 * Response containing signed URL for GCS upload
 */
export interface SignedUrlResponse {
    /**
     * ID of the transcript that will be created after upload
     */
    'transcriptId': string;
    /**
     * Signed URL for uploading the audio file to GCS (resumable upload)
     */
    'uploadUrl': string;
    /**
     * URL expiration timestamp (epoch milliseconds)
     */
    'expiresAt': number;
}
/**
 * The role of a speaker in an online recording: the microphone is the behandelaar, the call is the cliënt (#4639). 
 */

export const SpeakerRole = {
    Behandelaar: 'BEHANDELAAR',
    Client: 'CLIENT'
} as const;

export type SpeakerRole = typeof SpeakerRole[keyof typeof SpeakerRole];


/**
 * Transcription engine for live/streaming transcription via WebSocket. Tradeoff between quality and latency: - `whisper-large-v3-streaming`: Highest quality, higher latency - `whisper-medium-streaming`: Balanced quality and latency (default for streaming) - `whisper-small-streaming`: Lower quality, lowest latency - `whisper-tiny-streaming`: CPU-viable, accuracy too low for real   recordings — exists for the e2e harness (#1177) 
 */

export const StreamingEngine = {
    WhisperLargeV3Streaming: 'whisper-large-v3-streaming',
    WhisperMediumStreaming: 'whisper-medium-streaming',
    WhisperSmallStreaming: 'whisper-small-streaming',
    WhisperTinyStreaming: 'whisper-tiny-streaming'
} as const;

export type StreamingEngine = typeof StreamingEngine[keyof typeof StreamingEngine];


/**
 * Response containing transcript details
 */
export interface TranscriptResponse {
    /**
     * Unique transcript identifier
     */
    'id': string;
    /**
     * Organization this transcript belongs to
     */
    'organizationId': string;
    /**
     * Appointment this transcript is associated with (if applicable)
     */
    'appointmentId'?: string | null;
    /**
     * Form submission this transcript is associated with (if applicable)
     */
    'formSubmissionId'?: string | null;
    /**
     * Firebase UID of the user who initiated the transcription
     */
    'firebaseUid': string;
    'status': TranscriptStatus;
    /**
     * Language code used for transcription
     */
    'language'?: string;
    /**
     * Complete transcribed text (available when status is completed)
     */
    'fullText'?: string | null;
    /**
     * Audio duration in milliseconds (available when status is completed)
     */
    'durationMs'?: number | null;
    /**
     * Number of transcript segments (available when status is completed)
     */
    'segmentCount'?: number | null;
    /**
     * Error message (available when status is failed)
     */
    'errorMessage'?: string | null;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
    /**
     * Completion timestamp (epoch milliseconds, available when status is completed)
     */
    'completedAt'?: number | null;
}


/**
 * A single segment of the transcript with timing and speaker information
 */
export interface TranscriptSegment {
    /**
     * Speaker identifier (0-indexed)
     */
    'speakerLabel': number;
    /**
     * Who this speaker is, from the source their voice came through in an online recording (#4639). Null for a recording that was not online, and for a speaker whose voice could not be attributed to one source. 
     */
    'speakerRole'?: SpeakerRole | null;
    /**
     * Transcribed text for this segment
     */
    'text': string;
    /**
     * Start time of the segment in milliseconds
     */
    'startTimeMs': number;
    /**
     * End time of the segment in milliseconds
     */
    'endTimeMs': number;
}


/**
 * Processing status of the transcript: - `RECORDING`: Streaming session in progress (ADR-021) - `PENDING`: Waiting to be processed - `PROCESSING`: Currently being transcribed - `COMPLETED`: Transcription finished successfully - `FAILED`: Transcription failed 
 */

export const TranscriptStatus = {
    Recording: 'RECORDING',
    Pending: 'PENDING',
    Processing: 'PROCESSING',
    Completed: 'COMPLETED',
    Failed: 'FAILED'
} as const;

export type TranscriptStatus = typeof TranscriptStatus[keyof typeof TranscriptStatus];


/**
 * Transcription engine for batch/file processing. Tradeoff between quality and speed: - `whisper-large-v3`: Highest quality, slowest - `whisper-large-v2`: High quality, slightly faster - `whisper-medium`: Good quality, moderate speed - `whisper-small`: Lower quality, fastest - `whisper-tiny`: CPU-viable, accuracy too low for real recordings —   exists for the e2e harness (#1177)  Omit to let the service pick: the engine defaults to the deployment\'s configured `DEFAULT_TRANSCRIPTION_ENGINE`, which the e2e harness points at `whisper-tiny`. Naming a fixed default here instead would override that per-deployment setting (ADR-022). 
 */

export const TranscriptionEngine = {
    WhisperLargeV3: 'whisper-large-v3',
    WhisperLargeV2: 'whisper-large-v2',
    WhisperMedium: 'whisper-medium',
    WhisperSmall: 'whisper-small',
    WhisperTiny: 'whisper-tiny'
} as const;

export type TranscriptionEngine = typeof TranscriptionEngine[keyof typeof TranscriptionEngine];



/**
 * HealthApi - axios parameter creator
 */
export const HealthApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the health status of the Whisper transcription service.
         * @summary Health check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        healthCheck: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/health`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers from process state alone — it opens no connection and asks the database nothing.  This is what Cloud Run\'s `liveness_probe` reads. A liveness failure *recycles the container*, so it may only depend on things a restart can fix; a database stall is not one of them. `/health` is what gates traffic (`startup_probe`) and what the frontend and `whisper:deploy` read, and it deliberately still reports the database (#3872, #3496).  The process being able to serve this at all is the whole assertion: a response means the event loop is running and accepting requests. 
         * @summary Liveness check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        livenessCheck: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/health/live`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * HealthApi - functional programming interface
 */
export const HealthApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = HealthApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the health status of the Whisper transcription service.
         * @summary Health check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async healthCheck(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<HealthResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.healthCheck(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HealthApi.healthCheck']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers from process state alone — it opens no connection and asks the database nothing.  This is what Cloud Run\'s `liveness_probe` reads. A liveness failure *recycles the container*, so it may only depend on things a restart can fix; a database stall is not one of them. `/health` is what gates traffic (`startup_probe`) and what the frontend and `whisper:deploy` read, and it deliberately still reports the database (#3872, #3496).  The process being able to serve this at all is the whole assertion: a response means the event loop is running and accepting requests. 
         * @summary Liveness check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async livenessCheck(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LivenessResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.livenessCheck(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['HealthApi.livenessCheck']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * HealthApi - factory interface
 */
export const HealthApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = HealthApiFp(configuration)
    return {
        /**
         * Returns the health status of the Whisper transcription service.
         * @summary Health check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        healthCheck(options?: RawAxiosRequestConfig): AxiosPromise<HealthResponse> {
            return localVarFp.healthCheck(options).then((request) => request(axios, basePath));
        },
        /**
         * Answers from process state alone — it opens no connection and asks the database nothing.  This is what Cloud Run\'s `liveness_probe` reads. A liveness failure *recycles the container*, so it may only depend on things a restart can fix; a database stall is not one of them. `/health` is what gates traffic (`startup_probe`) and what the frontend and `whisper:deploy` read, and it deliberately still reports the database (#3872, #3496).  The process being able to serve this at all is the whole assertion: a response means the event loop is running and accepting requests. 
         * @summary Liveness check
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        livenessCheck(options?: RawAxiosRequestConfig): AxiosPromise<LivenessResponse> {
            return localVarFp.livenessCheck(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * HealthApi - object-oriented interface
 */
export class HealthApi extends BaseAPI {
    /**
     * Returns the health status of the Whisper transcription service.
     * @summary Health check
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public healthCheck(options?: RawAxiosRequestConfig) {
        return HealthApiFp(this.configuration).healthCheck(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers from process state alone — it opens no connection and asks the database nothing.  This is what Cloud Run\'s `liveness_probe` reads. A liveness failure *recycles the container*, so it may only depend on things a restart can fix; a database stall is not one of them. `/health` is what gates traffic (`startup_probe`) and what the frontend and `whisper:deploy` read, and it deliberately still reports the database (#3872, #3496).  The process being able to serve this at all is the whole assertion: a response means the event loop is running and accepting requests. 
     * @summary Liveness check
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public livenessCheck(options?: RawAxiosRequestConfig) {
        return HealthApiFp(this.configuration).livenessCheck(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * InternalApi - axios parameter creator
 */
export const InternalApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Receives GCS Object Finalize events via Eventarc. Triggered when an audio file upload completes in the transcription bucket.  **Internal endpoint:** Authenticated via `X-Service-Key` header, not Firebase JWT. Do not call directly from frontend. 
         * @summary GCS Object Finalize webhook
         * @param {GcsObjectFinalizeEvent} gcsObjectFinalizeEvent 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        handleGcsFinalize: async (gcsObjectFinalizeEvent: GcsObjectFinalizeEvent, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'gcsObjectFinalizeEvent' is not null or undefined
            assertParamExists('handleGcsFinalize', 'gcsObjectFinalizeEvent', gcsObjectFinalizeEvent)
            const localVarPath = `/internal/events/gcs-finalize`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication serviceKey required
            await setApiKeyToObject(localVarHeaderParameter, "X-Service-Key", configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(gcsObjectFinalizeEvent, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * InternalApi - functional programming interface
 */
export const InternalApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = InternalApiAxiosParamCreator(configuration)
    return {
        /**
         * Receives GCS Object Finalize events via Eventarc. Triggered when an audio file upload completes in the transcription bucket.  **Internal endpoint:** Authenticated via `X-Service-Key` header, not Firebase JWT. Do not call directly from frontend. 
         * @summary GCS Object Finalize webhook
         * @param {GcsObjectFinalizeEvent} gcsObjectFinalizeEvent 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async handleGcsFinalize(gcsObjectFinalizeEvent: GcsObjectFinalizeEvent, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GcsEventResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.handleGcsFinalize(gcsObjectFinalizeEvent, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InternalApi.handleGcsFinalize']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * InternalApi - factory interface
 */
export const InternalApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = InternalApiFp(configuration)
    return {
        /**
         * Receives GCS Object Finalize events via Eventarc. Triggered when an audio file upload completes in the transcription bucket.  **Internal endpoint:** Authenticated via `X-Service-Key` header, not Firebase JWT. Do not call directly from frontend. 
         * @summary GCS Object Finalize webhook
         * @param {GcsObjectFinalizeEvent} gcsObjectFinalizeEvent 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        handleGcsFinalize(gcsObjectFinalizeEvent: GcsObjectFinalizeEvent, options?: RawAxiosRequestConfig): AxiosPromise<GcsEventResponse> {
            return localVarFp.handleGcsFinalize(gcsObjectFinalizeEvent, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * InternalApi - object-oriented interface
 */
export class InternalApi extends BaseAPI {
    /**
     * Receives GCS Object Finalize events via Eventarc. Triggered when an audio file upload completes in the transcription bucket.  **Internal endpoint:** Authenticated via `X-Service-Key` header, not Firebase JWT. Do not call directly from frontend. 
     * @summary GCS Object Finalize webhook
     * @param {GcsObjectFinalizeEvent} gcsObjectFinalizeEvent 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public handleGcsFinalize(gcsObjectFinalizeEvent: GcsObjectFinalizeEvent, options?: RawAxiosRequestConfig) {
        return InternalApiFp(this.configuration).handleGcsFinalize(gcsObjectFinalizeEvent, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * UploadsApi - axios parameter creator
 */
export const UploadsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Generates a signed URL for uploading an audio file directly to GCS. The signed URL supports resumable uploads for reliable transfer of large files.  After the file is uploaded, GCS triggers a finalize event that automatically starts transcription processing. The Kotlin backend fetches results via the internal `GET /internal/transcripts/{transcriptId}` endpoint. 
         * @summary Initiate audio file upload
         * @param {InitiateUploadRequest} initiateUploadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        initiateUpload: async (initiateUploadRequest: InitiateUploadRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'initiateUploadRequest' is not null or undefined
            assertParamExists('initiateUpload', 'initiateUploadRequest', initiateUploadRequest)
            const localVarPath = `/api/v1/uploads/initiate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(initiateUploadRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UploadsApi - functional programming interface
 */
export const UploadsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = UploadsApiAxiosParamCreator(configuration)
    return {
        /**
         * Generates a signed URL for uploading an audio file directly to GCS. The signed URL supports resumable uploads for reliable transfer of large files.  After the file is uploaded, GCS triggers a finalize event that automatically starts transcription processing. The Kotlin backend fetches results via the internal `GET /internal/transcripts/{transcriptId}` endpoint. 
         * @summary Initiate audio file upload
         * @param {InitiateUploadRequest} initiateUploadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async initiateUpload(initiateUploadRequest: InitiateUploadRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignedUrlResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.initiateUpload(initiateUploadRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UploadsApi.initiateUpload']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * UploadsApi - factory interface
 */
export const UploadsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = UploadsApiFp(configuration)
    return {
        /**
         * Generates a signed URL for uploading an audio file directly to GCS. The signed URL supports resumable uploads for reliable transfer of large files.  After the file is uploaded, GCS triggers a finalize event that automatically starts transcription processing. The Kotlin backend fetches results via the internal `GET /internal/transcripts/{transcriptId}` endpoint. 
         * @summary Initiate audio file upload
         * @param {InitiateUploadRequest} initiateUploadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        initiateUpload(initiateUploadRequest: InitiateUploadRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignedUrlResponse> {
            return localVarFp.initiateUpload(initiateUploadRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * UploadsApi - object-oriented interface
 */
export class UploadsApi extends BaseAPI {
    /**
     * Generates a signed URL for uploading an audio file directly to GCS. The signed URL supports resumable uploads for reliable transfer of large files.  After the file is uploaded, GCS triggers a finalize event that automatically starts transcription processing. The Kotlin backend fetches results via the internal `GET /internal/transcripts/{transcriptId}` endpoint. 
     * @summary Initiate audio file upload
     * @param {InitiateUploadRequest} initiateUploadRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public initiateUpload(initiateUploadRequest: InitiateUploadRequest, options?: RawAxiosRequestConfig) {
        return UploadsApiFp(this.configuration).initiateUpload(initiateUploadRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



